import { useMutation } from '@tanstack/react-query';
import { ReactNode, useEffect, useState } from 'preact/compat';

import { loadViralLoops, ViralLoops } from '@/lib/viral-loops';
import { patchUserMutationFn } from '@/queries/users';
import { useSelector, useStore } from '@/redux/helper';
import { selectIsReferralParticipant } from '@/redux/selectors/users';
import { setUserData } from '@/redux/slices/user/actions';
import { buildURL, get } from '@/shared/fetch';

import ViralLoopsFormWidget from '../ReferralButton/ViralLoopsFormWidget';
import ReferralContext from './contexts';

const ReferralProvider = ({ children }: { children: ReactNode }) => {
  const store = useStore();
  const viralLoopsCampaignId = useSelector((state) => state.remoteConfig.viralLoopsCampaignId);
  // try to get viral loops from window first, if not found, it would be set by the useEffect below
  const [viralLoops, setViralLoops] = useState<ViralLoops | undefined>(
    typeof window !== 'undefined' ? window.ViralLoops : undefined,
  );
  const email = useSelector((state) => state.user.email);
  const isReferralParticipant = useSelector(selectIsReferralParticipant);

  const { mutate: patchUser } = useMutation({
    mutationFn: patchUserMutationFn,
    onSuccess: () => {
      get(buildURL('/users/user'))
        .then((user) => store.dispatch(setUserData(user)))
        .catch(console.error);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  useEffect(() => {
    if (viralLoopsCampaignId) {
      loadViralLoops(viralLoopsCampaignId);
    }
  }, [viralLoopsCampaignId]);

  useEffect(() => {
    // if viral loops is already loaded, set it to the state and no need to listen to the event
    if (window.ViralLoops) {
      setViralLoops(window.ViralLoops);
      return undefined;
    }
    const handleViralLoopsReady = () => {
      setViralLoops(window.ViralLoops);
    };
    window.addEventListener('vlReady', handleViralLoopsReady);
    return () => {
      window.removeEventListener('vlReady', handleViralLoopsReady);
    };
  }, []);

  useEffect(() => {
    if (!isReferralParticipant || !email) {
      return undefined;
    }
    // sometimes viral loops is not ready for getting campaign, so we retry a few times before giving up
    let trialsLeft = 5;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const handler = () => {
      viralLoops
        ?.getCampaign()
        .then((campaign) => {
          if (!campaign && trialsLeft > 0) {
            trialsLeft -= 1;
            timeoutId = setTimeout(handler, 2000);
            return undefined;
          }
          const { userId, subscription, name, username } = store.getState().user;
          const { stripeCurrency: currency } = subscription ?? {};
          return campaign?.identify({ email, firstname: name || username || undefined, extraData: { userId, currency } });
        })
        .then((viralLoopsData) => {
          if (!viralLoopsData) return;
          const { referralCode } = viralLoopsData;
          const currentUserReferralCode = store.getState().user.referralData?.viralLoops?.referralCode;
          if (currentUserReferralCode !== referralCode) {
            patchUser({ referralData: { viralLoops: { referralCode } } });
          }
        })
        .catch((error) => {
          console.error(error);
        });
    };
    handler();
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [email, isReferralParticipant, patchUser, store, viralLoops]);

  return (
    <ReferralContext.Provider value={viralLoops}>
      {children}
      {isReferralParticipant && viralLoopsCampaignId && (
        <ViralLoopsFormWidget campaignId={viralLoopsCampaignId} popup customButtonTrigger />
      )}
    </ReferralContext.Provider>
  );
};

export default ReferralProvider;
